import { clientApi } from '@/config/axios';

/**
 * Presigned grant returned by POST /organizations/{orgId}/uploads/presign.
 */
export interface PresignedUpload {
  upload_url: string;
  file_url: string;
  object_name: string;
  content_type: string;
  expires_in: number;
}

/**
 * Result of a completed direct-to-S3 upload — the reference the caller hands to
 * whichever endpoint consumes the file (e.g. schedule `/analyze`).
 *
 * `objectName` is the unique server-generated filename — pass it along when the
 * consumer needs a short, collision-free identifier for the upload.
 */
export interface UploadToS3Result {
  fileUrl: string;
  objectName: string;
  contentType: string;
  fileName: string;
}

/**
 * Upload a file directly to S3 via a presigned URL. Reusable across features
 * (schedule extraction today, document-generation audio later) — the `purpose`
 * selects the server-side key prefix + allowed types.
 *
 * 1. Request a presigned grant from the API for `purpose`.
 * 2. PUT the bytes straight to S3 with native `fetch` (not `clientApi`), so the
 *    app's auth/JSON interceptors aren't sent to S3. The presigned signature
 *    pins the Content-Type, so the header must match the grant exactly.
 */
export async function uploadToS3(
  orgId: string,
  file: File,
  purpose: string,
): Promise<UploadToS3Result> {
  const contentType = file.type || 'application/octet-stream';

  const { data: grant } = await clientApi.post<PresignedUpload>(
    `/api/v1/organizations/${orgId}/uploads/presign`,
    { purpose, content_type: contentType, filename: file.name },
  );

  const uploadRes = await fetch(grant.upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': grant.content_type },
  });
  if (!uploadRes.ok) {
    throw new Error(`Upload failed (${uploadRes.status})`);
  }

  return {
    fileUrl: grant.file_url,
    objectName: grant.object_name,
    contentType: grant.content_type,
    fileName: file.name,
  };
}
